<script setup lang="ts">
import { useIntersectionObserver } from "@vueuse/core";
import { nextTick, computed, ref, watch, onMounted, useTemplateRef, type Component } from "vue";
import CustomLoader from "~/components/ui/CustomLoader.vue";
import { ArtShowingEntryType } from "~/types/art-feed.types";
import SetCard from "./SetCard.vue";
import StandaloneCard from "./StandaloneCard.vue";
import MasonryWall from "../MasonryWall.vue";
import { useStaggerReveal } from "~/composables/useStaggerReveal";
import { useAnalytics } from "~/composables/useAnalytics";
import { usePreloadedInfiniteScroll } from "~/composables/usePreloadedInfiniteScroll";
import { getArtworkImageSrc, getArtworkSetImageSrc } from "~/utils";

export interface ArtworkFilters {
  priceMin?: number;
  priceMax?: number;
  sizeMin?: number;
  sizeMax?: number;
  orientation?: "portrait" | "landscape" | "square_circle";
  medium?: string[];
  colors?: string[];
  excludeSold?: boolean;
  fastDispatch?: boolean;
  onlySets?: boolean;
}

const props = defineProps<{
  /** If provided, artist ID filter will be applied */
  artistId?: string | null;
  /** If provided, selection ID filter will be applied to fetch artworks */
  selectionId?: string | null;
  /** If provided, artwork IDs filter will be applied  */
  artworkIds?: string[] | null;
  /** If provided, this artwork/set ID is excluded from the results */
  excludeArtworkId?: string | number | null;
  /** If provided, search query filter will be applied */
  searchQuery?: string;
  emptyStateComponent?: Component;
  /** Sort order: 'price_asc' | 'price_desc' */
  sortBy?: string;
  /** Active filters: priceMin, priceMax */
  filters?: ArtworkFilters;
  /** Context identifier for analytics */
  listContext?: string;
  /** Section identifier for analytics */
  section?: string;
  /** Optional callback to observe rendered artwork element for visibility */
  onObserveArtwork?: (el: Element | null, id: string | number | undefined) => void;
}>();

// Leading/trailing spaces in the search box must not change the search
const trimmedSearch = computed(() => (props.searchQuery ?? "").trim());

const { trackNoResultsViewed, trackCatalogueLoadDepth } = useAnalytics();

const effectiveListContext = computed(() => {
  if (props.listContext) return props.listContext;
  if (props.artistId) return "artist_profile_artworks";
  if (props.selectionId) return "selection_items";
  if (props.excludeArtworkId) return "related_artworks";
  return "artworks_catalogue";
});

const PAGE_SIZE = 20;

// Persist seed across navigations within the session so the default feed doesn't re-shuffle
// when navigating back from artwork details
const feedSeed = useState<string>("artworkFeedCatalogueSeed", () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).substring(2)
);

const feedCacheKey = computed(() => {
  if (props.artistId) {
    return `feed_artist_${props.artistId}_${trimmedSearch.value}_${props.sortBy || ""}_${JSON.stringify(props.filters || {})}`;
  }
  if (props.selectionId) {
    return `feed_selection_${props.selectionId}_${trimmedSearch.value}_${props.sortBy || ""}_${JSON.stringify(props.filters || {})}`;
  }
  if (props.artworkIds && props.artworkIds.length > 0) {
    return `feed_ids_${props.artworkIds.join(",")}_${props.sortBy || ""}`;
  }
  if (props.excludeArtworkId) {
    return `feed_exclude_${props.excludeArtworkId}_${props.artistId || ""}`;
  }
  return `feed_catalogue_${feedSeed.value}_${trimmedSearch.value}_${props.sortBy || ""}_${JSON.stringify(props.filters || {})}`;
});

const preloadArtworkImages = (artworks: any[]) => {
  if (!import.meta.client || !Array.isArray(artworks)) return;
  for (const art of artworks) {
    const photo = art.photo_names?.[0] || art.photo_name;
    if (photo) {
      const src =
        art.entry_type === ArtShowingEntryType.SET
          ? getArtworkSetImageSrc(photo, 360)
          : getArtworkImageSrc(photo, 360);
      const img = new Image();
      img.src = src;
    }
  }
};

const {
  items: flatItems,
  displayedPages,
  isLoading,
  hasNextPage,
  fetchNextPage,
} = usePreloadedInfiniteScroll({
  cacheKey: feedCacheKey,
  pageSize: PAGE_SIZE,
  preloadAhead: 2,
  fetchPage: async (pageParam: number) => {
    if (props.selectionId) {
      const params: any = {
        selectionId: props.selectionId,
        page: pageParam,
      };
      if (trimmedSearch.value) params.searchQuery = trimmedSearch.value;
      if (props.sortBy) params.sortBy = props.sortBy;
      if (props.filters?.priceMin !== undefined) params.priceMin = props.filters.priceMin;
      if (props.filters?.priceMax !== undefined) params.priceMax = props.filters.priceMax;
      if (props.filters?.sizeMin !== undefined) params.sizeMin = props.filters.sizeMin;
      if (props.filters?.sizeMax !== undefined) params.sizeMax = props.filters.sizeMax;
      if (props.filters?.orientation !== undefined) params.orientation = props.filters.orientation;
      if (props.filters?.medium && props.filters.medium.length > 0) params.medium = props.filters.medium;
      if (props.filters?.colors && props.filters.colors.length > 0) params.colors = props.filters.colors;
      if (props.filters?.excludeSold) params.excludeSold = props.filters.excludeSold;
      if (props.filters?.fastDispatch) params.fastDispatch = props.filters.fastDispatch;
      if (props.filters?.onlySets) params.onlySets = props.filters.onlySets;

      return await $fetch<any[]>("/api/selections/artworks", {
        method: "POST",
        body: params,
      });
    }

    const params: any = { page: pageParam, seed: feedSeed.value };
    if (trimmedSearch.value) params.searchQuery = trimmedSearch.value;
    if (props.artistId) params.artistId = props.artistId;
    if (props.artworkIds && props.artworkIds.length > 0) {
      params.artworkIds = props.artworkIds.join(",");
    }
    if (props.excludeArtworkId !== undefined && props.excludeArtworkId !== null) {
      params.excludeArtworkId = String(props.excludeArtworkId);
    }
    if (props.sortBy) params.sortBy = props.sortBy;
    if (props.filters?.priceMin !== undefined) params.priceMin = props.filters.priceMin;
    if (props.filters?.priceMax !== undefined) params.priceMax = props.filters.priceMax;
    if (props.filters?.sizeMin !== undefined) params.sizeMin = props.filters.sizeMin;
    if (props.filters?.sizeMax !== undefined) params.sizeMax = props.filters.sizeMax;
    if (props.filters?.orientation !== undefined) params.orientation = props.filters.orientation;
    if (props.filters?.medium && props.filters.medium.length > 0) params.medium = props.filters.medium;
    if (props.filters?.colors && props.filters.colors.length > 0) params.colors = props.filters.colors;
    if (props.filters?.excludeSold) params.excludeSold = props.filters.excludeSold;
    if (props.filters?.fastDispatch) params.fastDispatch = props.filters.fastDispatch;
    if (props.filters?.onlySets) params.onlySets = props.filters.onlySets;

    return await $fetch<any[]>("/api/artwork-feed", {
      method: "POST",
      body: params,
    });
  },
  onPageLoaded: (pageData) => {
    preloadArtworkImages(pageData);
  },
  watchSources: [
    () => props.selectionId,
    () => trimmedSearch.value,
    () => props.artistId,
    () => props.artworkIds,
    () => props.excludeArtworkId,
    () => props.sortBy,
    () => props.filters,
  ],
  onReset: () => {
    prevItemCount = 0;
  },
});

// --- For infinite scrolling
const infiniteLoaderRef = useTemplateRef("infiniteLoader");

useIntersectionObserver(
  infiniteLoaderRef,
  ([{ isIntersecting }]) => {
    if (isIntersecting && hasNextPage.value) {
      const nextPage = (displayedPages.value?.length ?? 0) + 1;
      trackCatalogueLoadDepth({
        list_context: effectiveListContext.value,
        depth_page: nextPage,
        items_count: flatItems.value.length,
      });
      fetchNextPage();
    }
  },
  {
    rootMargin: "800px 0px 800px 0px",
  }
);

const gridRef = ref<HTMLElement | null>(null);
const { reveal } = useStaggerReveal();

// Track how many items were visible before the latest update
let prevItemCount = 0;

// When grid DOM element mounts into the DOM (e.g. after loading completes)
watch(gridRef, (el) => {
  if (el && flatItems.value.length) {
    prevItemCount = flatItems.value.length;
    nextTick(() => {
      reveal(gridRef, { fresh: true });
    });
  }
});

// Fires on initial load AND on every new page from infinite scroll
watch(flatItems, (list) => {
  if (!list.length) return;
  // If this is the very first page (prevItemCount was 0), treat as fresh
  // If it's a new page appended, fresh=false so existing items aren't reset
  const isFreshLoad = prevItemCount === 0;
  prevItemCount = list.length;
  nextTick(() => {
    reveal(gridRef, { fresh: isFreshLoad });
  });
});

// Fallback: data already cached on mount
onMounted(() => {
  if (flatItems.value.length && gridRef.value) {
    prevItemCount = flatItems.value.length;
    reveal(gridRef, { fresh: true });
  }
});

// Tells the page once the first artworks are in (or the first load has finished), so it can wait to show
// the search bar. It fires once and not again while typing in the search reloads the list.
const emit = defineEmits<{ loaded: [] }>();
let announcedLoaded = false;
watchEffect(() => {
  if (announcedLoaded || (isLoading.value && !flatItems.value.length)) return;
  announcedLoaded = true;
  emit("loaded");
});
</script>

<template>
  <div v-if="isLoading && !flatItems.length" class="flex items-center justify-center py-16">
    <CustomLoader />
  </div>
  <div v-else-if="flatItems.length" ref="gridRef">
    <MasonryWall
      :items="flatItems"
      :column-width="250"
      :max-columns="4"
      :min-columns="2"
      :gapX="32"
      :gapY="32"
    >
      <template #default="{ item: artwork, index }">
        <div
          class="stagger-card stagger-item"
          :ref="(el) => props.onObserveArtwork?.(el as Element | null, artwork.id)"
        >
          <SetCard
            v-if="artwork.entry_type === ArtShowingEntryType.SET"
            :artwork="artwork"
            :list-context="effectiveListContext"
            :section="props.section"
            :position="index + 1"
            :filters="props.filters"
            :sort-by="props.sortBy"
          />
          <StandaloneCard
            v-else
            :artwork="artwork"
            :list-context="effectiveListContext"
            :section="props.section"
            :position="index + 1"
            :filters="props.filters"
            :sort-by="props.sortBy"
          />
        </div>
      </template>
    </MasonryWall>
    <div
      v-if="hasNextPage"
      ref="infiniteLoader"
      class="flex items-center justify-center py-16"
    >
      <CustomLoader />
    </div>
  </div>
  <template v-else>
    <!-- Empty state -->
    <template v-if="props.emptyStateComponent">
      <component :is="props.emptyStateComponent" />
    </template>
    <template v-else-if="!isLoading">
      <slot name="empty">
        <p class="text-center font-medium">No artworks found</p>
      </slot>
    </template>
  </template>
</template>
